import { useAuth } from 'context/auth';
import {
  enrichmentProviders,
  IEnrichmentProvider,
} from 'crono-fe-common/components/EnrichmentLoaderSkeleton/types';
import { useGetExternalEnrichmentProviders } from 'hooks/services/externalEnrichmentProviders/useGetExternalEnrichmentProviders';
import { FC, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { EnabledProvidersContext } from './contexts';

export const defaultEmailEnrichmentProviders: IEnrichmentProvider[] =
  enrichmentProviders.filter((provider) =>
    provider.dataProvided.includes('email'),
  );

export const defaultPhoneEnrichmentProviders: IEnrichmentProvider[] =
  enrichmentProviders.filter((provider) =>
    provider.dataProvided.includes('phone'),
  );

export const defaultContactLinkedInEnrichmentProviders: IEnrichmentProvider[] =
  enrichmentProviders.filter((provider) =>
    provider.dataProvided.includes('contact linkedIn url'),
  );

export const defaultCompanyLinkedInEnrichmentProviders: IEnrichmentProvider[] =
  enrichmentProviders.filter((provider) =>
    provider.dataProvided.includes('company linkedIn url'),
  );

const externalProviderConfigs = enrichmentProviders.filter((p) => p.external);

const EnabledProvidersProvider: FC<PropsWithChildren> = ({ children }) => {
  const [enabledEmailProviders, setEnabledEmailProviders] = useState<
    IEnrichmentProvider[]
  >([]);
  const [enabledPhoneProviders, setEnabledPhoneProviders] = useState<
    IEnrichmentProvider[]
  >([]);
  const [enabledContactLinkedInProviders, setEnabledContactLinkedInProviders] =
    useState<IEnrichmentProvider[]>([]);
  const [enabledCompanyLinkedInProviders, setEnabledCompanyLinkedInProviders] =
    useState<IEnrichmentProvider[]>([]);

  const { user } = useAuth();
  const { data: externalEnrichmentProvidersData } =
    useGetExternalEnrichmentProviders();

  useEffect(() => {
    if (!user || !user.otherSettings) return;

    const alreadyEnabledProviders: string[] | null | undefined =
      user.otherSettings.enabledProviders;

    if (alreadyEnabledProviders === undefined) return;

    const enabledExternals = (externalEnrichmentProvidersData?.data?.data ?? [])
      .filter((p) => p.enabled)
      .sort((a, b) => a.displayOrder - b.displayOrder);

    const getExternalConfig = (
      externalProvider: (typeof enabledExternals)[0],
    ) =>
      externalProviderConfigs.find(
        (config) => config.key === externalProvider.externalProviderType,
      );

    const toConfig = (p: (typeof enabledExternals)[0]) => getExternalConfig(p);

    const externalEmailBefore = enabledExternals
      .filter((p) => p.enrichEmail && p.beforeCronoProviders)
      .map(toConfig)
      .filter((c): c is IEnrichmentProvider => !!c);
    const externalEmailAfter = enabledExternals
      .filter((p) => p.enrichEmail && !p.beforeCronoProviders)
      .map(toConfig)
      .filter((c): c is IEnrichmentProvider => !!c);

    const externalPhoneBefore = enabledExternals
      .filter((p) => p.enrichPhone && p.beforeCronoProviders)
      .map(toConfig)
      .filter((c): c is IEnrichmentProvider => !!c);
    const externalPhoneAfter = enabledExternals
      .filter((p) => p.enrichPhone && !p.beforeCronoProviders)
      .map(toConfig)
      .filter((c): c is IEnrichmentProvider => !!c);

    const externalContactLinkedInBefore = enabledExternals
      .filter((p) => p.enrichLinkedin && p.beforeCronoProviders)
      .map(toConfig)
      .filter((c): c is IEnrichmentProvider => !!c);
    const externalContactLinkedInAfter = enabledExternals
      .filter((p) => p.enrichLinkedin && !p.beforeCronoProviders)
      .map(toConfig)
      .filter((c): c is IEnrichmentProvider => !!c);

    const externalCompanyLinkedInBefore = enabledExternals
      .filter((p) => p.enrichCompany && p.beforeCronoProviders)
      .map(toConfig)
      .filter((c): c is IEnrichmentProvider => !!c);
    const externalCompanyLinkedInAfter = enabledExternals
      .filter((p) => p.enrichCompany && !p.beforeCronoProviders)
      .map(toConfig)
      .filter((c): c is IEnrichmentProvider => !!c);

    if (alreadyEnabledProviders === null) {
      setEnabledEmailProviders([
        ...externalEmailBefore,
        ...defaultEmailEnrichmentProviders,
        ...externalEmailAfter,
      ]);
      setEnabledPhoneProviders([
        ...externalPhoneBefore,
        ...defaultPhoneEnrichmentProviders,
        ...externalPhoneAfter,
      ]);
      setEnabledContactLinkedInProviders([
        ...externalContactLinkedInBefore,
        ...defaultContactLinkedInEnrichmentProviders,
        ...externalContactLinkedInAfter,
      ]);
      setEnabledCompanyLinkedInProviders([
        ...externalCompanyLinkedInBefore,
        ...defaultCompanyLinkedInEnrichmentProviders,
        ...externalCompanyLinkedInAfter,
      ]);
      return;
    }

    const filteredEmailProviders = defaultEmailEnrichmentProviders.filter(
      (provider) => alreadyEnabledProviders.includes(provider.key),
    );
    const filteredPhoneProviders = defaultPhoneEnrichmentProviders.filter(
      (provider) => alreadyEnabledProviders.includes(provider.key),
    );
    const filteredContactLinkedInProviders =
      defaultContactLinkedInEnrichmentProviders.filter((provider) =>
        alreadyEnabledProviders.includes(provider.key),
      );
    const filteredCompanyLinkedInProviders =
      defaultCompanyLinkedInEnrichmentProviders.filter((provider) =>
        alreadyEnabledProviders.includes(provider.key),
      );

    setEnabledEmailProviders([
      ...externalEmailBefore,
      ...filteredEmailProviders,
      ...externalEmailAfter,
    ]);
    setEnabledPhoneProviders([
      ...externalPhoneBefore,
      ...filteredPhoneProviders,
      ...externalPhoneAfter,
    ]);
    setEnabledContactLinkedInProviders([
      ...externalContactLinkedInBefore,
      ...filteredContactLinkedInProviders,
      ...externalContactLinkedInAfter,
    ]);
    setEnabledCompanyLinkedInProviders([
      ...externalCompanyLinkedInBefore,
      ...filteredCompanyLinkedInProviders,
      ...externalCompanyLinkedInAfter,
    ]);
  }, [user?.otherSettings?.enabledProviders, externalEnrichmentProvidersData]);

  return (
    <EnabledProvidersContext.Provider
      value={{
        enabledEmailProviders,
        enabledPhoneProviders,
        enabledContactLinkedInProviders,
        enabledCompanyLinkedInProviders,
      }}
    >
      {children}
    </EnabledProvidersContext.Provider>
  );
};

export function useEnabledProviders() {
  const context = useContext(EnabledProvidersContext);
  if (context === undefined) {
    throw new Error(
      `useEnabledProviders must be used within EnabledProvidersProvider`,
    );
  }
  return context;
}

export default EnabledProvidersProvider;
